import { Component } from '@theme/component';
import { getStore } from '@theme/it-pdp-store';

/**
 * IT PDP — colourway swatches, when colour is a real option on the product.
 * (The sibling-product variant renders plain links and needs no script.)
 */
class ItPdpColorways extends Component {
  #unsubscribe;

  connectedCallback() {
    super.connectedCallback();

    this.#store = getStore(this);
    this.addEventListener('click', this.#onClick);

    const root = this.closest('[data-it-pdp]') ?? document;
    root.addEventListener('it:nudge-option', this.#onNudge);
    this.#root = root;

    if (!this.#store) return;

    this.#value = this.querySelector('.it-cw__value');
    this.#prompt = this.#value?.textContent.trim() ?? '';

    // A single swatch is marked active in the markup; tell the store, so the
    // page counts colour as answered and the CTA can go live.
    const only = this.querySelector('.it-cw__sw[data-value].is-active');
    if (only && this.querySelectorAll('.it-cw__sw[data-value]').length === 1) {
      this.#store.selectOption(only.dataset.optionName, only.dataset.value);
    }

    this.#unsubscribe = this.#store.subscribe(this.#render);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unsubscribe?.();
    this.#root?.removeEventListener('it:nudge-option', this.#onNudge);
  }

  #store = null;
  #root = null;
  #value = null;
  #prompt = '';

  #onClick = (event) => {
    const swatch = event.target.closest('.it-cw__sw[data-value]');
    if (!swatch) return;

    this.#store?.selectOption(swatch.dataset.optionName, swatch.dataset.value);
  };

  /**
   * The buy block asks for the colour (it-pdp-buy.js #askFor). Bring the row
   * on screen, shake it once and put focus on the first colour that can be
   * bought — exactly what the size picker does for a size. Until now nothing
   * listened for a colour ask at all, so a press on the CTA with a size
   * chosen and no colour changed nothing on the page.
   */
  #onNudge = (event) => {
    const first = this.querySelector('.it-cw__sw[data-value]');
    if (!first || event.detail?.optionName !== first.dataset.optionName) return;

    const row = this.querySelector('.it-cw__row') ?? this;
    row.scrollIntoView({ block: 'center', behavior: 'smooth' });

    // Restart the animation: removing the class alone does not rewind it.
    row.classList.remove('is-asking');
    void row.offsetWidth;
    row.classList.add('is-asking');

    (this.querySelector('.it-cw__sw[data-value]:not(.is-out)') ?? first).focus({ preventScroll: true });
  };

  #render = ({ selections }) => {
    const swatches = Array.from(this.querySelectorAll('.it-cw__sw[data-value]'));
    const [first] = swatches;
    if (!first) return;

    const optionName = first.dataset.optionName;
    const chosen = selections[optionName];
    const available = this.#store.availableValues(optionName);

    for (const swatch of swatches) {
      const active = swatch.dataset.value === chosen;
      swatch.classList.toggle('is-active', active);
      swatch.classList.toggle('is-out', !available.has(swatch.dataset.value));
      swatch.setAttribute('aria-pressed', String(active));
    }

    // Falls back to the prompt so deselecting never leaves a stale colour name
    // standing next to an unselected swatch row.
    if (this.#value) {
      this.#value.textContent = chosen || this.#prompt;
      this.#value.classList.toggle('it-cw__value--empty', !chosen);
    }
  };
}

if (!customElements.get('it-pdp-colorways')) {
  customElements.define('it-pdp-colorways', ItPdpColorways);
}
